import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { 
  ArrowLeft,
  Award, 
  TrendingUp,
  Building,
  Users,
  Linkedin
} from "lucide-react";

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen">
      {/* Header Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#112b3c]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="/credentra-logo-new.png" 
                alt="Credentra Logo" 
                className="h-52 w-auto"
              />
            </div>
            <div className="nav-links">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBackToHome}
                className="text-white hover:text-credentra-bright-teal font-medium px-4 py-2 ml-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* About Page Content */}
      <section className="text-white py-20 relative overflow-hidden" style={{background: '#112b3c', paddingTop: '120px'}}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* About Badge */}
          <div className="text-center pt-2 md:pt-1 pb-8 sm:pb-12">
            <div className="hero-badge inline-flex items-center gap-2 mb-8 sm:mb-12" style={{background: '#112b3c'}}>
              <Users className="h-4 w-4" />
              <span className="leading-tight">
                About Credentra
              </span>
            </div>
          </div>
          
          {/* Mission Statement */}
          <div className="text-center hero-content">
            <h1 className={`hero-title ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.1s'}}>
              <span>Our Mission</span>
            </h1>
            
            <div className={`max-w-4xl mx-auto mb-16 ${isVisible ? 'animate-slide-up' : ''}`} style={{animationDelay: '0.2s'}}>
              <div className="bg-white/10 rounded-2xl p-8 border border-white/20">
                <p className="text-xl leading-relaxed text-white/90">
                  Running a profitable restaurant is hard, to say the least. We get it. Every dollar matters when you're managing inventory, cash flow and labor. Credentra's mission is to help fuel America's hospitality small businesses by creating more profitability with tax savings, so hard-working owners like you can start re-investing in growth.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">

            <h2 className="enterprise-heading text-4xl md:text-5xl font-bold text-credentra-dark-teal mb-6">
              Founding Team
            </h2>
            <p className="enterprise-subheading text-xl max-w-2xl mx-auto">
              Built by executives with domain expertise across applied AI, financial services and hospitality.
            </p>
          </div>
          
          {/* Team Members Grid */}
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            
            {/* Jonathan Michael */}
            <div className="enterprise-card p-8 md:p-10 relative overflow-hidden">
              <div className="relative z-10">
                <div className="text-center space-y-6">
                  {/* Profile Picture */}
                  <div className="w-64 h-64 mx-auto mb-6 relative">
                    <div className="w-64 h-64 rounded-full overflow-hidden shadow-2xl border-4 border-credentra-bright-teal/20 bg-white">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_hospitality-tax/artifacts/epr7aoa5_Black%20and%20White%20Modern%20Linkedln%20Profile%20Picture%20%284%29.png"
                        alt="Jonathan Michael - Founder & CEO"
                        className="w-full h-full object-cover object-center"
                        style={{width: '256px', height: '256px'}}
                      />
                    </div>
                    {/* LinkedIn Icon */}
                    <a 
                      href="https://www.linkedin.com/in/jmjonathanmichael/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="absolute -bottom-4 -right-4 w-12 h-12 bg-[#0A66C2] hover:bg-[#004182] rounded-lg flex items-center justify-center transition-colors duration-300 shadow-lg"
                    >
                      <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </a>
                  </div>

                  {/* Bio Content */}
                  <div className="space-y-4">
                    <h3 className="text-3xl font-bold text-credentra-dark-teal">Jonathan Michael</h3>
                    <div>
                      <p className="text-lg font-semibold text-white px-4 py-2 rounded-lg inline-block" style={{background: '#112b3c'}}>Founder & CEO</p>
                      <p className="text-base text-gray-600 font-medium mt-1">Credentra AI</p>
                    </div>
                    
                    <p className="text-base text-gray-600 leading-relaxed">
                      Jonathan is an applied-AI builder who's been shipping AI products since 2019, launching Wealth I/O and later founding AdvisorX AI for RIAs. He brings deep expertise in data pipelines, compliance workflows, and GTM for financial services, frequently appearing on industry shows like LifeYield's AI-Augmented Advisor.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mark Benusa */}
            <div className="enterprise-card p-8 md:p-10 relative overflow-hidden">
              <div className="relative z-10">
                <div className="text-center space-y-6">
                  {/* Profile Picture */}
                  <div className="w-64 h-64 mx-auto mb-6 relative">
                    <div className="w-64 h-64 rounded-full overflow-hidden shadow-2xl border-4 border-credentra-bright-teal/20 bg-white">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_hospitality-tax/artifacts/e31jkbm9_Black%20and%20White%20Modern%20Linkedln%20Profile%20Picture%20%282%29.png"
                        alt="Mark Benusa - Founding Advisor"
                        className="w-full h-full object-cover object-center"
                        style={{width: '256px', height: '256px'}}
                      />
                    </div>
                    {/* LinkedIn Icon - Placeholder for consistent styling */}
                    <div className="absolute -bottom-4 -right-4 w-12 h-12 bg-gray-400 rounded-lg flex items-center justify-center shadow-lg opacity-50">
                      <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                  </div>

                  {/* Bio Content */}
                  <div className="space-y-4">
                    <h3 className="text-3xl font-bold text-credentra-dark-teal">Mark Benusa</h3>
                    <div>
                      <p className="text-lg font-semibold text-white px-4 py-2 rounded-lg inline-block" style={{background: '#112b3c'}}>Founding Advisor</p>
                      <p className="text-base text-gray-600 font-medium mt-1">Fine Creek Brewing Company</p>
                    </div>
                    
                    <p className="text-base text-gray-600 leading-relaxed">
                      Mark is the co-founder and co-owner of Fine Creek Brewing Company in Powhatan, Virginia—a destination farmhouse brewery he launched with his family in 2017 next to their wedding venue, The Mill at Fine Creek. As a seasoned hospitality entrepreneur and early Credentra client, he provides valuable insights into restaurant operations and helps shape product development.
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 text-white relative overflow-hidden" style={{background: '#112b3c'}}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Tax Recovery Process?
          </h2>
          <p className="text-xl mb-10 text-white/90">
            Join forward-thinking hospitality businesses that are already benefiting from FICA tip credit recovery.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg"
              className="text-white font-semibold text-lg px-10 py-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
              style={{background: 'linear-gradient(135deg, #00d6cf, #3c778d)', boxShadow: '0 4px 20px rgba(0, 214, 207, 0.3)'}}
              onClick={() => window.open('https://calendly.com/advisorxai/30min', '_blank')}
            >
              <Building className="mr-2 h-5 w-5" />
              Schedule a Demo
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              className="text-credentra-bright-teal border-credentra-bright-teal hover:bg-credentra-bright-teal hover:text-white font-semibold text-lg px-8 py-6 rounded-lg transition-all duration-300"
              onClick={handleBackToHome}
            >
              Back to Home
            </Button>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;